import { forwardRef, useEffect, useImperativeHandle, useState } from "react";

import { App_Url_New } from "@/shared/common/static";
import { ConfirmationCheckbox } from "@/shared/components";
import { DocumentUploadGrid } from "@/shared/components/DocumentUploadGrid";
import type { StepFormComponentProps, StepFormHandle } from "@/shared/stepper/types";
import type {
  DocumentUploadField,
  FieldKey,
  PersistedUploadedDocument,
} from "@/shared/types/documentuploadtype";
import type { HealthDocumentUploadValues } from "../../redux/type";

const HEALTH_DOCUMENT_FIELDS: Array<DocumentUploadField<HealthDocumentUploadValues>> = [
  {
    key: "previousPolicy",
    label: "Previous Policy",
    title: "Previous Policy",
    helper: "Click to upload (PDF / JPG / PNG)",
    required: false,
    icon: App_Url_New.DocumentIcons.idCard,
    enableOcr: false,
  },
  {
    key: "healthInsuranceCard",
    label: "Health Insurance Card",
    title: "Health Insurance Card",
    helper: "Click to upload (PDF / JPG / PNG)",
    required: false,
    icon: App_Url_New.DocumentIcons.idCard,
    enableOcr: false,
  },
  {
    key: "medicalReport",
    label: "Medical Report",
    title: "Medical Report",
    helper: "Click to upload (PDF / JPG / PNG)",
    required: false,
    icon: App_Url_New.DocumentIcons.pdf,
    enableOcr: false,
  },
];

const DOCUMENT_GUIDELINES = [
  "Accepted formats: PDF, JPG, PNG",
  "Maximum file size: 5 MB per document",
  "Ensure all details are clearly visible",
  "Blurry or cropped documents may delay approval",
];

const DEFAULT_FORM_VALUES: HealthDocumentUploadValues = {
  previousPolicy: false,
  healthInsuranceCard: false,
  medicalReport: false,
  confirmDocuments: false,
};

type HealthDocumentUploadStepExtraProps = {
  isReadOnly?: boolean;
  persistedDocuments?: Partial<Record<string, PersistedUploadedDocument<string>>>;
  onUploadComplete?: (params: {
    field: DocumentUploadField<HealthDocumentUploadValues>;
    file: File;
    uploadResponse?: unknown;
    ocrResponse?: unknown;
    persistedDocument: PersistedUploadedDocument<FieldKey<HealthDocumentUploadValues>>;
  }) => void;
  isShowUploadGuidelines?: boolean;
  showRequiredIdentificationTitle?: boolean;
  onConfirmDocumentsChange?: (checked: boolean) => boolean | Promise<boolean> | void;
  enableAutoConfirmWhenRequiredUploaded?: boolean;
};

const HealthDocumentUploadStep = forwardRef<
  StepFormHandle<HealthDocumentUploadValues>,
  StepFormComponentProps<HealthDocumentUploadValues, HealthDocumentUploadStepExtraProps>
>(function HealthDocumentUploadStep(
  {
    defaultValues: incomingValues,
    isReadOnly = false,
    persistedDocuments,
    onUploadComplete,
    isShowUploadGuidelines = true,
    showRequiredIdentificationTitle = true,
    onConfirmDocumentsChange,
    enableAutoConfirmWhenRequiredUploaded = false,
  },
  ref
) {
  const [form, setForm] = useState<HealthDocumentUploadValues>(
    incomingValues ?? DEFAULT_FORM_VALUES
  );
  const [hasSubmitAttempted, setHasSubmitAttempted] = useState(false);
  const [showConfirmError, setShowConfirmError] = useState(false);
  const [uploadErrors, setUploadErrors] = useState<Partial<Record<string, string>>>({});

  useEffect(() => {
    setForm(incomingValues ?? DEFAULT_FORM_VALUES);
    setHasSubmitAttempted(false);
    setShowConfirmError(false);
    setUploadErrors({});
  }, [incomingValues]);

  useEffect(() => {
    if (!persistedDocuments) return;

    setForm((current) => {
      const nextValues = { ...current };
      HEALTH_DOCUMENT_FIELDS.forEach((field) => {
        if (persistedDocuments[field.key]) {
          nextValues[field.key] = true;
        }
      });
      return nextValues;
    });
  }, [persistedDocuments]);

  const getUploadValidationErrors = (values: HealthDocumentUploadValues) =>
    HEALTH_DOCUMENT_FIELDS.reduce<Partial<Record<string, string>>>((errors, field) => {
      if (field.required && values[field.key] !== true) {
        errors[field.key] = `${field.label || field.title} is required.`;
      }
      return errors;
    }, {});

  const shouldAutoConfirmDocuments = (values: HealthDocumentUploadValues) =>
    HEALTH_DOCUMENT_FIELDS.filter((field) => field.required).every(
      (field) => values[field.key] === true
    );

  const normalizeUploadedValues = (values: HealthDocumentUploadValues) => {
    if (
      !enableAutoConfirmWhenRequiredUploaded ||
      values.confirmDocuments === true ||
      !shouldAutoConfirmDocuments(values)
    ) {
      return values;
    }

    void onConfirmDocumentsChange?.(true);
    return {
      ...values,
      confirmDocuments: true,
    };
  };

  const syncValidationState = (nextValues: HealthDocumentUploadValues) => {
    if (!hasSubmitAttempted) return;
    setUploadErrors(getUploadValidationErrors(nextValues));
    setShowConfirmError(nextValues.confirmDocuments !== true);
  };

  useImperativeHandle(ref, () => ({
    submit: async () => {
      if (isReadOnly) {
        return true;
      }
      const nextUploadErrors = getUploadValidationErrors(form);
      const isConfirmValid = form.confirmDocuments === true;

      setHasSubmitAttempted(true);
      setUploadErrors(nextUploadErrors);
      setShowConfirmError(!isConfirmValid);

      return Object.keys(nextUploadErrors).length === 0 && isConfirmValid;
    },
    getValues: () => form,
  }));

  return (
    <div className="space-y-6">
      {showRequiredIdentificationTitle ? (
        <div className="mb-4">
          <h2 className="text-lg text-dark">Required Identification</h2>
        </div>
      ) : null}

      <fieldset disabled={isReadOnly} className="contents">
        <DocumentUploadGrid<HealthDocumentUploadValues>
          fields={HEALTH_DOCUMENT_FIELDS}
          values={form}
          disabled={isReadOnly}
          onChange={(nextValues) => {
            const normalizedValues = normalizeUploadedValues(nextValues);
            setForm(normalizedValues);
            syncValidationState(normalizedValues);
          }}
          fieldErrors={uploadErrors}
          uploadEndpoint={App_Url_New.ApiEndPoint.documentUpload}
          ocrEndpointPath={App_Url_New.ApiOcrEndPoint.ExtractDocument}
          ocrFields={{
            ai_engine: "openai",
            ocr_engine: "easy",
          }}
          initialDocuments={persistedDocuments}
          onUploadComplete={onUploadComplete}
        />
      </fieldset>
      {isShowUploadGuidelines && (
        <div className="rounded-xl border border-theme-border bg-theme-surface-subtle p-4 mt-5">
          <h3 className="text-sm font-semibold text-theme-text">Document guidelines</h3>
          <ul className="mt-3 list-disc space-y-2 pl-5 text-[11px] text-theme-text-soft">
            {DOCUMENT_GUIDELINES.map((guideline) => (
              <li className="mb-0.5" key={guideline}>
                {guideline}
              </li>
            ))}
          </ul>
        </div>
      )}

      <fieldset disabled={isReadOnly} className="contents">
        <ConfirmationCheckbox
          checked={form.confirmDocuments === true}
          disabled={isReadOnly}
          error={showConfirmError ? "Please confirm the document upload before proceeding." : ""}
          onChange={async (checked) => {
            if (checked) {
              const nextUploadErrors = getUploadValidationErrors(form);
              const hasValidationErrors = Object.keys(nextUploadErrors).length > 0;

              setHasSubmitAttempted(true);
              setUploadErrors(nextUploadErrors);

              if (hasValidationErrors) {
                setShowConfirmError(true);
                setForm((current) => ({
                  ...current,
                  confirmDocuments: false,
                }));
                return;
              }
            }

            const nextValues = { ...form, confirmDocuments: checked };
            setForm(nextValues);
            syncValidationState(nextValues);
            queueMicrotask(async () => {
              const accepted = await onConfirmDocumentsChange?.(checked);
              if (checked && accepted === false) {
                setForm((current) => {
                  const revertedValues = { ...current, confirmDocuments: false };
                  syncValidationState(revertedValues);
                  return revertedValues;
                });
              }
            });
          }}
        >
          I confirm that I have uploaded all required documents.
        </ConfirmationCheckbox>
      </fieldset>
    </div>
  );
});

export default HealthDocumentUploadStep;
